import { FC, useMemo } from 'react';

import { useListNextBestActionEligibleCampaigns } from 'modules/ai/nba/hooks/useNBAEligibleCampaignQuery';
import { useCampaignSendSizeData } from 'modules/campaigns/lib/hooks';
import { CampaignType } from 'modules/domains/campaign';
import { useCampaignIdParam, useCampaignProjectLabels, useQueryCampaignById } from '../../hooks';
import { CampaignContext, CampaignContextState } from './CampaignContext';
import { getIsNonPublishedEligibilityCampaign } from '../../utils';

export const CampaignProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const campaignId = useCampaignIdParam();

  const queryOptions = useMemo(
    () => ({
      skip: campaignId === 0,
      variables: { id: campaignId },
    }),
    [campaignId],
  );

  const {
    errorMessage = '',
    hasError,
    isLoading,
    refetch,
    results: campaign,
  } = useQueryCampaignById(queryOptions);

  const { data: nbaEnabledCampaignData } = useListNextBestActionEligibleCampaigns();

  const { expectedSendSizeData, estimatedSendSizeData } = useCampaignSendSizeData(
    campaignId,
    campaign?.lists?.length,
  );

  const { refetch: refetchCampaignProjectLabels, results: campaignProjectLabels } =
    useCampaignProjectLabels();

  // Embedded campaigns created through a journey will be of Eligibility type until the journey is published but the campaign editor
  // should treat it as WorkflowTriggered since the flow is different between the 2 and the campaign is effectively always WorkflowTriggered
  const mappedCampaign = {
    ...campaign,
    ...(campaign.campaignTypeDefinition
      ? {
          campaignTypeDefinition: getIsNonPublishedEligibilityCampaign(campaign)
            ? CampaignType.WorkflowTriggered
            : campaign.campaignTypeDefinition,
        }
      : {}),
  };

  const value: CampaignContextState = {
    errorMessage,
    hasError,
    isLoading,
    campaign: mappedCampaign,
    refetch: () => refetch({ id: campaignId }),
    expectedSendSizeData,
    estimatedSendSizeData,
    campaignProjectLabels: campaignProjectLabels || [],
    refetchCampaignProjectLabels,
    nbaEnabledCampaignIds:
      nbaEnabledCampaignData?.listNextBestActionEligibleCampaigns?.campaignIds || [],
  };

  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>;
};
